import React, { useContext, useState } from 'react';
import VotedCard from './VotedCard';
import { useGetItemTypes, useGetPlatformActionItem } from './hooks';
import * as S from './style';
import { ActionItemType } from '../../DomainEnums';
import { GlobalContext } from '../../context/GlobalState';
import { LoadingPage } from '../../components';
import { Card } from '../../@types/domain';

const getIcon = (workItemTypes, platformItem) =>
  workItemTypes?.find(
    (type) => type.name === platformItem.type || type.id === platformItem.type
  );

const transformCard = (platformItems, workItemTypes, card) => {
  const newActionItems = card.actionItems
    .map((actionItem) => {
      if (actionItem.type === ActionItemType.TEXT_NOTE) {
        return actionItem;
      }

      const platformItem = platformItems.find(
        (item) => item.id === actionItem.platformActionItemId
      );

      return {
        ...actionItem,
        id: actionItem.platformActionItemId ?? null,
        text:
          platformItem?.name ||
          `Item not found: it may have been deleted or you don't have permission to access it`,
        icon: (platformItem && getIcon(workItemTypes, platformItem)) ?? null,
        disabled: Boolean(!platformItem),
        key: platformItem?.key,
      };
    })
    .filter(Boolean);

  return {
    ...card,
    actionItems: newActionItems,
  };
};

const DiscussBoard = () => {
  const { cards } = useContext(GlobalContext);
  const [cardsModified, setCardsModified] = useState<Card[]>([]);
  const [isLoadingAll, setIsLoadingAll] = useState<boolean>(true);

  const {
    data: itemTypes,
    isLoading: isLoadingItemTypes,
    isSuccess: isSuccessLoadedItemTypes,
  } = useGetItemTypes();

  const { isLoading: isLoadingPlatformActionItems } = useGetPlatformActionItem({
    initialData: [],
    enabled: !!itemTypes && isSuccessLoadedItemTypes && cards.length > 0,
    refetchIntervalInBackground: false,
    refetchOnReconnect: true,
    async onSuccess(platformActionItems) {
      setIsLoadingAll(true);
      const newCards = cards
        .sort((card1, card2) => {
          if (card1.votes.length === card2.votes.length) {
            return card1.rank - card2.rank;
          }
          return card1.votes.length > card2.votes.length ? -1 : 1;
        })
        .map((card) => transformCard(platformActionItems, itemTypes, card));
      setCardsModified(newCards);
      setIsLoadingAll(false);
    },
  });

  const isLoading =
    isLoadingAll || isLoadingItemTypes || isLoadingPlatformActionItems;

  if (cards.length === 0) {
    return null;
  }

  return (
    <S.Container>
      {itemTypes &&
        isSuccessLoadedItemTypes &&
        !isLoading &&
        cardsModified.map((card, index) => (
          <VotedCard
            key={card.id + itemTypes.length}
            card={card}
            itemTypes={itemTypes}
            isFocused={index === 0}
          />
        ))}
      {isLoading && <LoadingPage />}
    </S.Container>
  );
};

export default DiscussBoard;
